import { effectivePortions, splitPortionsAt, MIN_PORTION_S } from './portions'
import { isProgram } from './itemKinds'
import type { Channel, BreakItem, QueueItem } from './types'

export interface BreakResolverState {
  lastBumper: string | null
}

/** Title shown for an ad row wherever queue items surface (guide marker). */
const AD_TITLE = 'AD BREAK'

/**
 * Bake ads into a built video queue at the channel's configured cadence. Walks
 * the (already play-ordered / shuffled) video list and inserts an ad QueueItem
 * after every `breaks.ads.every` videos, choosing from `breaks.ads.items` and
 * avoiding consecutive repeats. No trailing ad after the final video.
 *
 * This is the build-time replacement for the old runtime "show an ad every N
 * transitions" counter: ads are now first-class queue members, so the player
 * and the transition FSM treat them uniformly.
 */
export function injectAds(
  videos: QueueItem[],
  ch: Channel,
  adBumpersEnabled: boolean,
  random: () => number = Math.random,
): QueueItem[] {
  const ads = ch.breaks?.ads
  if (!adBumpersEnabled || !ads || !ads.enabled || !ads.items.length) return videos
  const every = Math.max(1, ads.every)

  const out: QueueItem[] = []
  let lastAd: string | null = null
  for (let i = 0; i < videos.length; i++) {
    out.push(videos[i])
    const isBoundary = (i + 1) % every === 0
    const hasMore = i < videos.length - 1
    if (isBoundary && hasMore) {
      const choices = ads.items.filter((a) => a.scene !== lastAd)
      const pick = (choices.length ? choices : ads.items)[
        Math.floor(random() * (choices.length ? choices.length : ads.items.length))
      ]
      lastAd = pick.scene
      out.push({
        kind: 'scene',
        role: 'break',
        scene: pick.scene,
        params: pick.params,
        title: AD_TITLE,
      })
    }
  }
  return out
}

/** Pick an ad scene from the slot, avoiding the last one shown. Advances `random` once. */
function pickAd(items: BreakItem[], lastAd: string | null, random: () => number): BreakItem {
  const choices = items.filter((a) => a.scene !== lastAd)
  const pool = choices.length ? choices : items
  return pool[Math.floor(random() * pool.length)]
}

const adItem = (pick: BreakItem): QueueItem => ({ kind: 'scene', role: 'break', scene: pick.scene, params: pick.params, title: AD_TITLE })

/** Videos shorter than this (CONTENT-seconds) are never cut mid-clip — the ad waits until they end. */
export const MIN_SPLITTABLE_S = 300 // 5 minutes
/** Never cut within this many seconds of a video's start or end — too jarring; defer the ad instead. */
export const AD_EDGE_MARGIN_S = 60

/**
 * Seconds-based ad injection: insert an ad roughly every `everySeconds` of CONTENT (±`jitterPct`,
 * deterministic from the seeded `random`).
 *
 * A video is only CUT mid-clip when it's long enough to be worth splitting (≥ MIN_SPLITTABLE_S) AND
 * the target sits comfortably away from both edges (≥ AD_EDGE_MARGIN_S from start and end). Otherwise
 * the ad is DEFERRED to play whole-video-then-ad — so a 15s clip is never sliced, and we never cut a
 * sliver off a video's head or tail. The split is in video-seconds (via splitPortionsAt), so each half
 * is a normal multi-portion video item to the scheduler/player. No ad trails the final content.
 */
export function injectAdsBySeconds(
  videos: QueueItem[],
  ch: Channel,
  adBumpersEnabled: boolean,
  random: () => number = Math.random,
): QueueItem[] {
  const ads = ch.breaks?.ads
  if (!adBumpersEnabled || !ads || !ads.enabled || !ads.items.length || !ads.everySeconds) return videos
  const everySeconds = ads.everySeconds
  const jitterPct = ads.jitterPct ?? 0.15
  const interval = () => everySeconds * (1 + (random() * 2 - 1) * jitterPct)

  const out: QueueItem[] = []
  let lastAd: string | null = null
  let clock = 0 // content-seconds emitted so far
  let nextAdAt = interval() // first ad target (content-seconds)
  let pendingAd = false // a deferred ad waiting to play BETWEEN videos (dropped if no video follows → no trailing ad)

  for (const item of videos) {
    if (pendingAd) { const pick = pickAd(ads.items, lastAd, random); lastAd = pick.scene; out.push(adItem(pick)); pendingAd = false }
    if (item.kind !== 'video') { out.push(item); continue } // ads add no CONTENT time (input is video-only anyway)
    let cur = effectivePortions(item)
    let curDur = cur.reduce((a, p) => a + (p.end - p.start), 0)
    const itemStart = clock // content position where this whole video begins
    const itemDur = curDur
    const pieces: QueueItem[] = []

    while (nextAdAt < clock + curDur - MIN_PORTION_S) {
      const offsetInVideo = nextAdAt - itemStart // distance from the video's TRUE start
      const nearEdge = offsetInVideo < AD_EDGE_MARGIN_S || itemDur - offsetInVideo < AD_EDGE_MARGIN_S
      if (itemDur < MIN_SPLITTABLE_S || nearEdge) {
        // can't cut here (too short / too close to an edge) → hold the ad until this video is over.
        pendingAd = true
        nextAdAt = clock + curDur + interval() // next target measured from the end of this video
        break
      }
      const localCut = nextAdAt - clock // offset within the current remainder
      const [before, after] = splitPortionsAt(cur, localCut)
      pieces.push({ kind: 'video', videoId: item.videoId, title: item.title, durationSeconds: item.durationSeconds, portions: before })
      clock += localCut
      const pick = pickAd(ads.items, lastAd, random); lastAd = pick.scene
      pieces.push(adItem(pick))
      nextAdAt += interval()
      cur = after
      curDur -= localCut
    }

    if (pieces.length) {
      pieces.push({ kind: 'video', videoId: item.videoId, title: item.title, durationSeconds: item.durationSeconds, portions: cur })
      out.push(...pieces)
    } else {
      out.push(item) // untouched — preserves the whole-video / sponsor-portions item as-is
    }
    clock += curDur
  }
  return out // a still-pending ad after the final video is dropped (no trailing ad)
}

/** Count of PROGRAM items (any render kind; role !== 'break') in a queue — the listed shows. */
export function programCount(queue: QueueItem[]): number {
  return queue.reduce((n, q) => (isProgram(q) ? n + 1 : n), 0)
}

/**
 * Map a PROGRAM ordinal (0-based index counting only programs, as the guide/tune/
 * boot-resume think in) to its index in the mixed queue. Clamps to the last
 * program if out of range; returns 0 for an empty/program-less queue.
 */
export function programOrdinalToQueueIndex(queue: QueueItem[], ordinal: number): number {
  let seen = 0
  let lastProgram = 0
  for (let i = 0; i < queue.length; i++) {
    if (!isProgram(queue[i])) continue
    lastProgram = i
    if (seen === ordinal) return i
    seen++
  }
  return lastProgram
}

/** Inverse: the PROGRAM ordinal for a mixed-queue index (breaks count as the next program). */
export function queueIndexToProgramOrdinal(queue: QueueItem[], index: number): number {
  let seen = 0
  for (let i = 0; i < index && i < queue.length; i++) {
    if (isProgram(queue[i])) seen++
  }
  return seen
}

/**
 * Resolve the IDENT scene to cover a transition: 'main' for a channel tune,
 * 'program' for the now-playing inter-video ident overlay. (Ads are NOT resolved
 * here — they are baked into the queue by injectAds and rendered as content by the
 * playback FSM.) So this is INDEPENDENT of the `adBumpers` setting: that setting
 * gates ad INJECTION only; channel + program idents always resolve. Returns the
 * selected BreakItem and the updated selection state (lastBumper avoids showing the
 * same ident twice running).
 */
export function resolveBreak(
  ch: Channel,
  slotKind: 'main' | 'program',
  transitionReason: 'tune' | 'next' | 'jump',
  state: BreakResolverState,
  defaultChannelMs: number,
  defaultProgramMs: number,
): { item: BreakItem | null; nextState: BreakResolverState } {
  const defaultMs = transitionReason === 'next' ? defaultProgramMs : defaultChannelMs
  const fallback: BreakItem = { scene: ch.bumper || 'sweep', params: {}, maxMs: defaultMs, weight: 1 }

  const slot = ch.breaks?.[slotKind]
  if (!slot || !slot.items.length) {
    // 'program' has NO fallback (a missing program slot = no inter-video ident → no overlay; NEVER
    // the full-screen channel bumper, which would mis-render as a flashing "card"); 'main' always
    // falls back to the channel's default bumper (a tune always shows a channel ident).
    return { item: slotKind === 'program' ? null : fallback, nextState: state }
  }

  const items = slot.items.filter((i) => i.scene !== state.lastBumper)
  const item = items.length ? items[Math.floor(Math.random() * items.length)] : slot.items[0]
  return {
    item: item || null,
    nextState: { lastBumper: item ? item.scene : state.lastBumper },
  }
}
